/**
 * Clipboard for the single-furniture copy/paste flow (SRS REQ-368).
 *
 * Backed by `localStorage` so the copied object is shared across all same-origin tabs/windows:
 * copy in one tab and paste into another tab that has the SAME quotation open. (Paste is gated
 * by `hasClipboardFor(currentQuotationId)`, so a copy can't be pasted into a different
 * quotation.) The slot also survives reloads and browser restarts; only one slot exists at a
 * time — a second copy overwrites the first.
 *
 * The slot stores only the identifiers ({ quotationId, furnitureId }); paste re-reads the
 * live furniture from the quotation cache so it always duplicates the current source state.
 */

const STORAGE_KEY = "autoscan.furnitureClipboard"

export type TFurnitureClipboard = {
	quotationId: number
	furnitureId: number
}

const safeStorage = (): Storage | null => {
	try {
		return window.localStorage
	} catch {
		// Storage can be unavailable (privacy mode, sandboxed iframe). Degrade to no clipboard.
		return null
	}
}

/** Store the copied furniture for its quotation, replacing any previous slot. */
export const copyFurniture = (quotationId: number, furnitureId: number): void => {
	const store = safeStorage()
	if (!store) return
	const payload: TFurnitureClipboard = { quotationId, furnitureId }
	try {
		store.setItem(STORAGE_KEY, JSON.stringify(payload))
	} catch {
		/* quota / serialization error — copy silently fails */
	}
}

/** Read the current clipboard slot, or `null` when nothing valid is stored. */
export const readFurnitureClipboard = (): TFurnitureClipboard | null => {
	const store = safeStorage()
	if (!store) return null
	const raw = store.getItem(STORAGE_KEY)
	if (!raw) return null
	try {
		const parsed = JSON.parse(raw) as Partial<TFurnitureClipboard>
		if (typeof parsed?.quotationId === "number" && typeof parsed?.furnitureId === "number") {
			return { quotationId: parsed.quotationId, furnitureId: parsed.furnitureId }
		}
		return null
	} catch {
		return null
	}
}

/** Whether a copied furniture exists for the given quotation (paste-enabled gate). */
export const hasClipboardFor = (quotationId: number): boolean => {
	const slot = readFurnitureClipboard()
	return slot != null && slot.quotationId === quotationId
}

/** Clear the clipboard slot. */
export const clearFurnitureClipboard = (): void => {
	const store = safeStorage()
	if (!store) return
	try {
		store.removeItem(STORAGE_KEY)
	} catch {
		/* ignore */
	}
}
